import { haptic } from '@/lib/appBridge';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Cart, CartTotals } from '@/types';
import { cartApi } from '@/lib/api';
import toast from 'react-hot-toast';

/** Safely extract cart and totals from API response */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractCartData(resData: any): { cart: Cart; totals: CartTotals } {
  // API may return { cart, totals } or { data: { cart, totals } }
  const d = resData?.data ?? resData;
  const cart = d?.cart ?? resData?.cart ?? { items: [] };
  const totals = d?.totals ?? resData?.totals ?? { subtotal: 0, tax: 0, shipping: 0, discount: 0, loyalty_discount: 0, total: 0 };
  return { cart, totals };
}

/**
 * A guest cart item — used when an unauthenticated visitor adds items. Stored in
 * localStorage and merged into the server cart on sign-in. Shape mirrors what
 * the backend's POST /cart/items expects.
 */
export interface GuestCartItem {
  id: string; // local-only uuid
  product_id: number;
  design_id: number | null;
  variant_selections: Record<string, string>;
  quantity: number;
  // Cached snapshot for rendering the cart row without re-fetching the product.
  product_name?: string;
  unit_price?: number;
}

interface CartState {
  cart: Cart | null;
  totals: CartTotals | null;
  isLoading: boolean;
  itemCount: number;
  /** Guest items kept in localStorage. Cleared on sign-in after merge. */
  guestItems: GuestCartItem[];
  /** Optional guest coupon code. */
  guestCoupon: string | null;
  fetchCart: () => Promise<void>;
  addItem: (
    productId: number,
    designId: number | null,
    variants: Record<string, string>,
    quantity: number
  ) => Promise<void>;
  /** Add an item to the guest cart (no API call). */
  addGuestItem: (item: Omit<GuestCartItem, 'id'>) => void;
  /** Push guest items to backend; clear local. Called on login success. */
  mergeGuestCart: () => Promise<void>;
  updateQuantity: (itemId: number, quantity: number) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  /** Resolves with the outcome so the coupon box can show it inline (toasts still fire). */
  applyCoupon: (code: string) => Promise<{ ok: boolean; message: string }>;
  removeCoupon: () => Promise<void>;
  toggleLoyalty: () => Promise<void>;
  clearCart: () => void;
}

export const useCartStore = create<CartState>()(persist((set, get) => ({
  cart: null,
  totals: null,
  isLoading: false,
  itemCount: 0,
  guestItems: [],
  guestCoupon: null,

  addGuestItem: (item) => {
    const id = `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    set((state) => ({
      guestItems: [...state.guestItems, { ...item, id }],
    }));
    toast.success('Added to cart!');
  },

  mergeGuestCart: async () => {
    const items = get().guestItems;
    if (items.length === 0) return;
    let pushed = 0;
    for (const item of items) {
      try {
        await cartApi.addItem({
          product_id: item.product_id,
          design_id: item.design_id ?? undefined,
          variant_selections: item.variant_selections,
          quantity: item.quantity,
        });
        pushed += 1;
      } catch {
        // ignore individual failures — others may still succeed
      }
    }
    set({ guestItems: [], guestCoupon: null });
    if (pushed > 0) {
      // Refresh from backend to reflect the merged state
      try {
        const response = await cartApi.get();
        const { cart, totals } = extractCartData(response.data);
        set({
          cart,
          totals,
          itemCount: cart?.items?.reduce((sum: number, it: { quantity: number }) => sum + it.quantity, 0) || 0,
        });
      } catch {
        // ignore
      }
    }
  },

  fetchCart: async () => {
    set({ isLoading: true });
    try {
      const response = await cartApi.get();
      const { cart, totals } = extractCartData(response.data);
      set({
        cart,
        totals,
        itemCount: cart?.items?.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0) || 0,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  addItem: async (productId, designId, variants, quantity) => {
    try {
      const response = await cartApi.addItem({
        product_id: productId,
        design_id: designId,
        variant_selections: variants,
        quantity,
      });
      const { cart, totals } = extractCartData(response.data);
      set({
        cart,
        totals,
        itemCount: cart?.items?.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0) || 0,
      });
      // Backend auto-clamps to min/max quantity for pack-sold products (e.g. business cards:
      // min 100). If it adjusted the qty, show the explanatory message as an info toast so the
      // user isn't surprised by the new count in their cart. Otherwise show the usual success.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body = response.data as any;
      if (body?.quantity_adjusted && body?.message) {
        toast(body.message, { icon: 'ℹ️', duration: 5000 });
      } else {
        toast.success('Added to cart!');
        haptic('medium');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const msg = axiosErr?.response?.data?.message || 'Failed to add item to cart.';
      toast.error(msg);
    }
  },

  updateQuantity: async (itemId, quantity) => {
    try {
      const response = await cartApi.updateItem(itemId, { quantity });
      const { cart, totals } = extractCartData(response.data);
      set({
        cart,
        totals,
        itemCount: cart?.items?.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0) || 0,
      });
      // Same pattern as addItem: surface the backend's clamp notice if it adjusted the qty.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body = response.data as any;
      if (body?.quantity_adjusted && body?.message) {
        toast(body.message, { icon: 'ℹ️', duration: 5000 });
      }
    } catch {
      toast.error('Failed to update quantity.');
    }
  },

  removeItem: async (itemId) => {
    try {
      const response = await cartApi.removeItem(itemId);
      const { cart, totals } = extractCartData(response.data);
      set({
        cart,
        totals,
        itemCount: cart?.items?.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0) || 0,
      });
      toast.success('Item removed from cart.');
    } catch {
      toast.error('Failed to remove item.');
    }
  },

  applyCoupon: async (code) => {
    try {
      const response = await cartApi.applyCoupon(code);
      const { cart, totals } = extractCartData(response.data);
      set({ cart, totals });
      toast.success('Coupon applied!');
      return { ok: true, message: `Coupon ${code} applied.` };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message || 'Invalid or expired coupon code.';
      toast.error(msg);
      return { ok: false, message: msg };
    }
  },

  removeCoupon: async () => {
    try {
      const response = await cartApi.removeCoupon();
      const { cart, totals } = extractCartData(response.data);
      set({ cart, totals });
      toast.success('Coupon removed.');
    } catch {
      toast.error('Failed to remove coupon.');
    }
  },

  toggleLoyalty: async () => {
    try {
      const response = await cartApi.toggleLoyalty();
      const { cart, totals } = extractCartData(response.data);
      set({ cart, totals });
    } catch {
      toast.error('Failed to toggle loyalty points.');
    }
  },

  clearCart: () => {
    set({ cart: null, totals: null, itemCount: 0, guestItems: [], guestCoupon: null });
  },
}), {
  name: 'esyprintz-guest-cart',
  storage: createJSONStorage(() => (typeof window !== 'undefined' ? window.localStorage : ({
    getItem: () => null,
    setItem: () => undefined,
    removeItem: () => undefined,
  } as unknown as Storage))),
  // Persist only the guest portion. Authenticated cart/totals live on the server
  // and are re-fetched on each load — persisting them would risk showing stale
  // amounts after a price change.
  partialize: (state) => ({
    guestItems: state.guestItems,
    guestCoupon: state.guestCoupon,
  }),
}));
